import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import { useSelector, shallowEqual } from 'react-redux';
import { number, string, func, bool, elementType } from 'prop-types';
import InputField from '#component/form/input';
import { normalizeNumberField } from '#component/shipment/form/cargo/helper';
import { convertLength, convertWeight, getLengthUnit, getWeightUnit } from '#helper';

const dimensionsFields = [
  { name: 'length', label: 'Length' },
  { name: 'width', label: 'Width' },
  { name: 'height', label: 'Height' }
];

const DimensionsWeightFields = props => {
  const { FieldsGroupCnt = 'div', member, setValue: propsSetValue, tareWeight = null, setTareWeight, disabled } = props;
  const methods = useFormContext();
  const { control, setValue: contextSetValue, formState } = methods || {};
  const setValue = propsSetValue || contextSetValue;

  const [touched, setTouched] = useState({});
  const measurementSystem = useSelector(s => s.account?.measurementSystem, shallowEqual);

  const handleDimensionsChange = name => {
    if (!touched[name]) {
      setValue?.(`${member}.container`, 'Custom');
      setTouched(t => ({ ...t, [name]: true }));
      setTareWeight(null);
    }
  };

  const handleDimensionsBlur = (name, value) => {
    const converted = convertLength(value, measurementSystem, 'metric') || null;
    setValue?.(name, converted);
    setTouched(t => ({ ...t, [name]: false }));
  };

  const handleWeightBlur = (name, value) => {
    const converted = convertWeight(value, measurementSystem, 'metric') || null;
    setValue?.(name, converted);
    setTouched(t => ({ ...t, [name]: false }));
  };

  return (
    <FieldsGroupCnt>
      <Grid container spacing={2}>
        {dimensionsFields.map(field => {
          const fieldName = `${member}.${field.name}`;
          return (
            <Grid item xs={12} sm={6} md={3} key={field.name}>
              <Controller
                name={fieldName}
                control={control}
                render={({ field: controllerField, fieldState }) => (
                  <InputField
                    {...controllerField}
                    meta={{ ...fieldState, isSubmitted: formState?.isSubmitted }}
                    label={field.label}
                    variant="outlined"
                    fullWidth
                    onBlur={e => handleDimensionsBlur(fieldName, e.target.value)}
                    onChange={e => {
                      controllerField.onChange(normalizeNumberField(e.target.value));
                      handleDimensionsChange(fieldName);
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{getLengthUnit(measurementSystem)}</InputAdornment>
                    }}
                    disabled={disabled}
                  />
                )}
              />
            </Grid>
          );
        })}
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name={`${member}.weight`}
            control={control}
            render={({ field, fieldState }) => (
              <InputField
                {...field}
                meta={{ ...fieldState, isSubmitted: formState?.isSubmitted }}
                label="Total weight"
                variant="outlined"
                fullWidth
                onBlur={e => handleWeightBlur(`${member}.weight`, e.target.value)}
                onChange={e => {
                  field.onChange(normalizeNumberField(e.target.value));
                }}
                info={tareWeight ? `Tare ${Math.round(100 * convertWeight(tareWeight, 'metric', measurementSystem)) / 100} ${getWeightUnit(measurementSystem)}` : null}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{getWeightUnit(measurementSystem)}</InputAdornment>
                }}
                disabled={disabled}
              />
            )}
          />
        </Grid>
      </Grid>
    </FieldsGroupCnt>
  );
};

export default DimensionsWeightFields;

DimensionsWeightFields.propTypes = {
  FieldsGroupCnt: elementType,
  member: string.isRequired,
  change: func.isRequired,
  tareWeight: number,
  setTareWeight: func.isRequired,
  disabled: bool
};
